import { useDispatch, useSelector } from "react-redux";
import Table from "../ui/table/TableController";
import {
  companiesSelector,
  updatePayment,
  updatePaymentState,
  // ⬇️ VAŽNO: u slice-u exportaj ova dva thunka
  getCardTypes2,
  getCardTypes3,
  getHouseAccounts,
  getOutlets,
} from "../../redux/slices/companiesSlice";
import { authSelector } from "../../redux/slices/authSlice";
import { uiSelector } from "../../redux/slices/uiSlice";
import Loading from "./tableState/Loading";
import NoSearch from "./tableState/NoSearch";
import NoItems from "./tableState/NoItems";
import React, { useEffect, useMemo, useRef, useState } from "react";

// ====== UI: pretraživi + skrolajući listbox (popover) s Clear ======
const SearchableListbox = ({
  items,
  onSelect,
  onClose,
  onClear,
  placeholder = "Search...",
  autoFocus = true,
  width = 340,
  maxHeight = 320,
}) => {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) onClose?.();
    };
    const onEsc = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items || [];
    return (items || []).filter((it) => {
      const idStr = String(it.id ?? "").toLowerCase();
      const nameStr = String(it.name ?? "").toLowerCase();
      return idStr.includes(q) || nameStr.includes(q);
    });
  }, [items, query]);

  useEffect(() => {
    if (activeIndex >= filtered.length) {
      setActiveIndex(Math.max(0, filtered.length - 1));
    }
  }, [filtered.length, activeIndex]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector(`[data-idx="${activeIndex}"]`);
    if (!el) return;
    const elTop = el.offsetTop;
    const elBottom = elTop + el.offsetHeight;
    const viewTop = list.scrollTop;
    const viewBottom = viewTop + list.clientHeight;
    if (elTop < viewTop) list.scrollTop = elTop;
    else if (elBottom > viewBottom) list.scrollTop = elBottom - list.clientHeight;
  }, [activeIndex, filtered]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = filtered[activeIndex];
      if (target) onSelect?.(target.id, target.name);
    }
  };

  return (
    <div
      ref={rootRef}
      style={{
        position: "absolute",
        zIndex: 1000,
        marginTop: 6,
        width,
        maxWidth: "calc(100vw - 32px)",
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        boxShadow:
          "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)",
      }}
      onKeyDown={handleKeyDown}
    >
      <div
        style={{
          padding: 8,
          borderBottom: "1px solid #f3f4f6",
          display: "flex",
          gap: 8,
          alignItems: "center",
        }}
      >
        <input
          autoFocus={autoFocus}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          style={{
            flex: 1,
            padding: "8px 10px",
            border: "1px solid #d1d5db",
            borderRadius: 6,
            outline: "none",
          }}
        />
        <button
          type="button"
          onClick={() => { onClear?.(); onClose?.(); }}
          title="Clear selection"
          style={{
            padding: "6px 10px",
            border: "1px solid #e5e7eb",
            borderRadius: 6,
            background: "#fafafa",
            cursor: "pointer",
          }}
        >
          Clear
        </button>
      </div>

      <div ref={listRef} style={{ maxHeight, overflowY: "auto", padding: 6 }}>
        {filtered.length === 0 ? (
          <div style={{ padding: 10, color: "#6b7280" }}>No results</div>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {filtered.map((opt, idx) => {
              const active = idx === activeIndex;
              return (
                <li key={`opt-${opt.value}`} data-idx={idx}>
                  <button
                    type="button"
                    onClick={() => onSelect?.(opt.id, opt.name)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "8px 10px",
                      border: "none",
                      background: active ? "#eef2ff" : "#fff",
                      cursor: "pointer",
                      borderRadius: 6,
                      whiteSpace: "nowrap",
                    }}
                    onMouseEnter={() => setActiveIndex(idx)}
                  >
                    {`${opt.id} - ${opt.name}`}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div
        style={{
          padding: "6px 10px",
          borderTop: "1px solid #f3f4f6",
          fontSize: 12,
          color: "#6b7280",
        }}
      >
        ↑/↓ to navigate, Enter to select, Esc to close
      </div>
    </div>
  );
};

const getListboxWidth = (items) => {
  const maxTextLength = Math.max(
    0,
    ...(items || []).map((item) => `${item.id} - ${item.name}`.length)
  );

  return Math.min(720, Math.max(360, maxTextLength * 8 + 72));
};
const ArrowCell = ({
  row,
  field,               // "pms_id" | "pms_account_id"
  items,               // [{ value, id, name }]
  isOpen,
  onToggle,
  onSelect,            // (id, name) => void
  onClear,             // () => void
}) => {
  const disabled = !items || items.length === 0;
  const listboxWidth = getListboxWidth(items);
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={disabled ? undefined : onToggle}
        title={disabled ? "No options" : "Open list"}
        aria-label="Open list"
        disabled={disabled}
        style={{
          padding: "2px 6px",
          border: "1px solid #d1d5db",
          borderRadius: 6,
          background: disabled ? "#f9fafb" : "#fff",
          color: disabled ? "#9ca3af" : "#111827",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        ▾
      </button>

      {!disabled && isOpen && (
        <SearchableListbox
          items={items}
          onSelect={(id, name) => onSelect(id, name)}
          onClear={onClear}
          onClose={onToggle}
          width={listboxWidth}
          maxHeight={320}
          placeholder="Search ID or Name..."
        />
      )}
    </div>
  );
};

const PaymentsSNT = () => {
  const {
    payments,
    tableLoading,
    cardTypesLoading,
    houseAccountsLoading,
    houseAccounts,
    cardTypes2,           // SNT_HOUSE_ACCOUNTS — za pms_id
    cardTypes3,           // SNT_CHARGE_CODES  — za pms_account_id
    cardTypes2Loading = false,
    cardTypes3Loading = false,
    currentId,
    outlets,
    outletsLoading = false,
  } = useSelector(companiesSelector);

  const dispatch = useDispatch();
  const { searchQuery } = useSelector(uiSelector);
  const { integraInfo } = useSelector(authSelector);
  const paymentIntegraInfo = String(
    payments?.xintegra ||
      payments?.find?.((payment) => payment?.xintegra)?.xintegra ||
      payments?.[0]?.xintegra ||
      integraInfo ||
      ""
  ).toUpperCase();
  const isMews = paymentIntegraInfo === "MEWSMICROS";
  const isWristcoinMews = paymentIntegraInfo === "WRISTCOINMEWS";
  const isShopifyOhip = paymentIntegraInfo === "SHOPIFYOHIP";

  // 🔔 DOHVATI LISTE AKO NISU UČITANE
  useEffect(() => {
    if (!cardTypes2?.length) dispatch(getCardTypes2());
    if (!cardTypes3?.length) dispatch(getCardTypes3());
    if (isMews && currentId) dispatch(getOutlets());
    if (isWristcoinMews && currentId) dispatch(getHouseAccounts());
  }, [dispatch, currentId, isMews, isWristcoinMews]); // refetch na promjenu tvrtke

  const [openMenu, setOpenMenu] = useState(null);
  const [tempChanges, setTempChanges] = useState({});

  const dedupByValue = (arr) => {
    const seen = new Set();
    return (arr || []).filter((x) => {
      if (!x || x.value === undefined || x.value === null) return false;
      const key = String(x.value);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // Robustne normalizacije (podržavaju objekte i string "ID|Name")
  const splitIdName = (str) => {
    const [idPart, ...rest] = String(str).split("|");
    const idStr = (idPart || "").trim();
    const nameStr = rest.join("|").trim() || idStr;
    const idNum = Number(idStr);
    return { id: Number.isNaN(idNum) ? idStr : idNum, name: nameStr };
  };

  const normalizeHouse = (item) => {
    if (!item) return null;
    if (typeof item === "string") {
      const { id, name } = splitIdName(item);
      return { value: id, id, name };
    }
    if (item.id != null && item.name != null)
      return { value: item.id, id: item.id, name: String(item.name) };
    if (item.outlet_id != null)
      return {
        value: item.outlet_id,
        id: item.outlet_id,
        name: String(item.outlet_name || item.outlet_id),
      };
    if (item.floor_id != null)
      return {
        value: item.floor_id,
        id: item.floor_id,
        name: String(item.floor_name || item.floor_id),
      };
    if (item.house_name) {
      const { id, name } = splitIdName(item.house_name);
      return { value: id, id, name };
    }
    if (item.pms_id != null)
      return {
        value: item.pms_id,
        id: item.pms_id,
        name: String(item.pms_name || item.pms_id),
      };
    if (item.code && item.title)
      return { value: item.code, id: item.code, name: String(item.title) };
    return null;
  };

  const normalizePms = (item) => {
    if (!item) return null;
    if (typeof item === "string") {
      const { id, name } = splitIdName(item);
      return { value: id, id, name };
    }
    if (item.id != null && item.name != null)
      return { value: item.id, id: item.id, name: String(item.name) };
    if (item.cardcode)
      return {
        value: item.cardcode,
        id: item.cardcode,
        name: item.cardname || item.cardcode,
      };
    if (item.code && item.title)
      return { value: item.code, id: item.code, name: String(item.title) };
    return null;
  };

  const houseArrowItems = useMemo(
    () =>
      dedupByValue(
        (isMews ? outlets || [] : isWristcoinMews ? houseAccounts || [] : cardTypes2 || [])
          .map(normalizeHouse)
          .filter(Boolean)
      ),
    [cardTypes2, houseAccounts, isMews, isWristcoinMews, outlets]
  );
  const pmsArrowItems = useMemo(
    () =>
      dedupByValue(
        (isMews ? houseAccounts || [] : cardTypes3 || [])
          .map((item) => {
            const normalized = isMews ? normalizeHouse(item) : normalizePms(item);
            if (!isMews || !normalized) return normalized;

            const fullValue = String(normalized.id).includes("|")
              ? normalized.id
              : `${normalized.id}|${normalized.name}`;
            return isMews
              ? { ...normalized, value: fullValue, id: fullValue }
              : normalized;
          })
          .filter(Boolean)
      ),
    [cardTypes3, houseAccounts, isMews]
  );

  const houseNameById = useMemo(() => {
    const m = new Map();
    houseArrowItems.forEach((i) => m.set(String(i.id), i.name));
    return m;
  }, [houseArrowItems]);

  const pmsNameById = useMemo(() => {
    const m = new Map();
    pmsArrowItems.forEach((i) => m.set(String(i.id), i.name));
    return m;
  }, [pmsArrowItems]);

  const formatPmsAccount = (id, name) =>
    id != null
      ? String(id).includes("|")
        ? id
        : `${id}${name ? `${isMews ? "|" : " - "}${name}` : ""}`
      : "";
  const getPmsIdApiValue = (id, name) => {
    if (!isMews || id == null || String(id).includes("|")) return id;

    const outletName = name || houseNameById.get(String(id));
    return outletName ? `${id}|${outletName}` : id;
  };

  const filteredPayments = useMemo(() => {
    const q = (searchQuery || "").toLowerCase();
    if (!q) return payments;
    return payments.filter(
      (i) =>
        String(i.payment_id).includes(q) ||
        i.payment_name.toLowerCase().includes(q) ||
        String(i.pms_id).includes(q)
    );
  }, [payments, searchQuery]);

  // 🔥 helper: spoji promjenu i odmah spremi (state + thunk)
  const saveChange = (row, partial) => {
    const merged = {
      ...row,
      ...(tempChanges[row.payment_id] || {}),
      ...partial,
    };

    // Rekalkuliraj display polja
    if (Object.prototype.hasOwnProperty.call(merged, "pms_id")) {
      const nm =
        merged.pms_id_name ??
        (merged.pms_id != null
          ? houseNameById.get(String(merged.pms_id))
          : "");
      merged.pms_id_display =
        merged.pms_id != null
          ? `${merged.pms_id}${nm ? ` - ${nm}` : ""}`
          : "";
    }
    if (Object.prototype.hasOwnProperty.call(merged, "pms_account_id")) {
      const nm =
        merged.pms_account_name ??
        (merged.pms_account_id != null
          ? pmsNameById.get(String(merged.pms_account_id))
          : "");
      merged.pms_account_id_display =
        formatPmsAccount(merged.pms_account_id, nm);
    }

    // 1) lokalni store state
    dispatch(updatePaymentState(merged));
    // 2) API save (thunk)
    dispatch(
      updatePayment({
        ...merged,
        pms_id: getPmsIdApiValue(merged.pms_id, merged.pms_id_name),
        pms_account_id: merged.pms_account_id,
      })
    );
  };

  const handleSelect = (row, field, id, name) => {
    const display =
      field === "pms_account_id" ? formatPmsAccount(id, name) : `${id} - ${name}`;
    // lokalno temp (za prikaz)
    setTempChanges((prev) => ({
      ...prev,
      [row.payment_id]: {
        ...(prev[row.payment_id] || {}),
        ...(field === "pms_id"
          ? {
              pms_id: id,
              pms_id_name: name,
              pms_id_display: display,
            }
          : field === "pms_account_id"
          ? {
              pms_account_id: id,
              pms_account_name: name,
              pms_account_id_display: display,
            }
          : {}),
      },
    }));
    // auto-save odmah
    if (field === "pms_id") {
      saveChange(row, {
        pms_id: id,
        pms_id_name: name,
        pms_id_display: display,
      });
    } else if (field === "pms_account_id") {
      saveChange(row, {
        pms_account_id: id,
        pms_account_name: name,
        pms_account_id_display: display,
      });
    }
    setOpenMenu(null);
  };

  const handleClear = (row, field) => {
    setTempChanges((prev) => ({
      ...prev,
      [row.payment_id]: {
        ...(prev[row.payment_id] || {}),
        ...(field === "pms_id"
          ? {
              pms_id: null,
              pms_id_name: "",
              pms_id_display: "",
            }
          : field === "pms_account_id"
          ? {
              pms_account_id: null,
              pms_account_name: "",
              pms_account_id_display: "",
            }
          : {}),
      },
    }));
    // auto-save odmah i na Clear
    if (field === "pms_id") {
      saveChange(row, {
        pms_id: null,
        pms_id_name: "",
        pms_id_display: "",
      });
    } else if (field === "pms_account_id") {
      saveChange(row, {
        pms_account_id: null,
        pms_account_name: "",
        pms_account_id_display: "",
      });
    }
    setOpenMenu(null);
  };

  // ⚠ withTemp je još uvijek korišten samo za prikaz i dropdown state-a.
  // Ali vrijednosti koje su KRITIČNE (pms_id, pms_account_id, start_date)
  // moraju doći iz API reda (row iz payments), ne iz UI-a.
  //
  // Znači: za prikaz možemo formatirati display string,
  // ali izbjegavamo da privremene promjene pregaze "source of truth".
  // ...
const withTemp = (rowFromApi) => {
  const tmp = tempChanges[rowFromApi.payment_id] || {};

  // Kritične vrijednosti (id-jevi i start_date) uzimamo isključivo iz API reda
  const pmsId = rowFromApi.pms_id;
  // ime house account-a: prvo iz reda, onda iz mape po ID-u
  const pmsIdName =
    rowFromApi.pms_id_name ??
    houseNameById.get(String(pmsId)) ??
    "";

  const pmsIdDisplay =
    pmsId != null
      ? `${pmsId}${pmsIdName ? ` - ${pmsIdName}` : ""}`
      : "";

  const pmsAccId = rowFromApi.pms_account_id;
  // ⛔ NEMA tmp.pms_account_name ovdje
  const pmsAccName =
    rowFromApi.pms_account_name ??
    (isMews ? rowFromApi.pms_name : undefined) ??
    pmsNameById.get(String(pmsAccId)) ??
    "";

  const pmsAccDisplay = formatPmsAccount(pmsAccId, pmsAccName);

  return {
    ...rowFromApi,

    // authoritative values
    pms_id: pmsId,
    pms_account_id: pmsAccId,
    start_date: rowFromApi.start_date,

    // only display strings (for table cells)
    pms_id_display: pmsIdDisplay,
    pms_account_id_display: pmsAccDisplay,

    // napomena: ostavljamo arrow cellove kasnije u dataWithArrows
  };
};
// ...


  const dataWithArrows = useMemo(() => {
    return filteredPayments.map((r) => {
      const row = withTemp(r);
      const isHouseOpen =
        openMenu?.rowId === row.payment_id && openMenu?.field === "pms_id";
      const isPmsAccOpen =
        openMenu?.rowId === row.payment_id && openMenu?.field === "pms_account_id";

      return {
        ...row,
        pms_id_arrow: (
          <ArrowCell
            row={row}
            field="pms_id"
            items={houseArrowItems}
            isOpen={isHouseOpen}
            onToggle={() =>
              setOpenMenu((prev) =>
                prev && prev.rowId === row.payment_id && prev.field === "pms_id"
                  ? null
                  : { rowId: row.payment_id, field: "pms_id" }
              )
            }
            onSelect={(id, name) => handleSelect(row, "pms_id", id, name)}
            onClear={() => handleClear(row, "pms_id")}
          />
        ),
        pms_account_id_arrow: (
          <ArrowCell
            row={row}
            field="pms_account_id"
            items={pmsArrowItems}
            isOpen={isPmsAccOpen}
            onToggle={() =>
              setOpenMenu((prev) =>
                prev &&
                prev.rowId === row.payment_id &&
                prev.field === "pms_account_id"
                  ? null
                  : { rowId: row.payment_id, field: "pms_account_id" }
              )
            }
            onSelect={(id, name) =>
              handleSelect(row, "pms_account_id", id, name)
            }
            onClear={() => handleClear(row, "pms_account_id")}
          />
        ),
      };
    });
  }, [filteredPayments, openMenu, tempChanges, houseArrowItems, pmsArrowItems]);

  const headerData = [
    { id: 132, label: "Company", width: "10%", sortable: false },
    { id: 232, label: "Id", width: "10%", sortable: true },
    { id: 342, label: "Name", width: "16%", sortable: true },
    ...(isWristcoinMews
      ? [
          { id: 3321, label: "Outlet", width: "12%", sortable: true },
          { id: 3322, label: "", width: "4%", sortable: false },
        ]
      : isMews
      ? [
          { id: 3321, label: "Outlet ID", width: "16%", sortable: true },
          { id: 3322, label: "", width: "4%", sortable: false },
        ]
      : [
          { id: 3321, label: "HOUSE ACCOUNT", width: "12%", sortable: true },
          { id: 3322, label: "", width: "4%", sortable: false },
          { id: 333, label: "RESERVATION ID", width: "10%", sortable: true },
        ]
      : []),
    {
      id: 362,
      label: isMews
        ? "Payment Method"
        : isWristcoinMews
        ? "Payment Type"
        : isShopifyOhip
        ? "Payment Method (Opera)"
        : "PMS Account ID",
      width: isMews ? "16%" : "12%",
      sortable: true,
    },
    { id: 363, label: "", width: "4%", sortable: false },
    ...(!isWristcoinMews
      ? [
          {
            id: 372,
            label: "Start Date (YYYY-MM-DD)",
            width: isMews ? "8%" : "16%",
            sortable: true,
          },
        ]
      : []),
    { id: 532, label: "Action", width: "10%", sortable: false },
  ];

  const rowKeys = [
    "company_id",
    "payment_id",
    "payment_name",
    ...(isWristcoinMews
      ? ["pms_id_display", "pms_id_arrow"]
      : isMews
      ? ["pms_id_display", "pms_id_arrow"]
      : ["pms_id_display", "pms_id_arrow", "reservation_id"]),
    "pms_account_id_display",
    "pms_account_id_arrow",
    ...(!isWristcoinMews ? ["start_date"] : []),
  ];

  const editable = [
    ...(!isMews && !isWristcoinMews && !isShopifyOhip ? ["reservation_id"] : []),
    ...(!isWristcoinMews ? ["start_date"] : []),
  ];

  const sortByData = [
    {},
    { label: "payment_id", type: "number" },
    { label: "payment_name", type: "string" },
    ...(isWristcoinMews
      ? [{ label: "pms_id_display", type: "string" }, {}]
      : isMews
      ? [{ label: "pms_id_display", type: "string" }, {}]
      : [
          { label: "pms_id_display", type: "string" },
          {},
          { label: "reservation_id", type: "number" },
        ]
      : []),
    { label: "pms_account_id_display", type: "string" },
    {},
    ...(!isWristcoinMews ? [{ label: "start_date", type: "string" }] : []),
  ];

  // ❗ Ovdje je ključna promjena:
  // kad korisnik klikne "Action" (ručni save reda):
  // - UVIJEK čitamo pms_id, pms_account_id i start_date iz originalnog reda x (API state),
  //   ne iz tempChanges / ekrana.
  const updateAction = (x) => {
  const pending = tempChanges[x.payment_id];

  // kreni od onoga što je već u store-u (tj. zadnja potvrđena istina sa servera)
  const mergedFromApi = { ...x };

  // Dozvoljeno ručno: reservation_id (jer user to tipka)
  if (pending) {
    if (Object.prototype.hasOwnProperty.call(pending, "reservation_id")) {
      mergedFromApi.reservation_id = pending.reservation_id;
    }
    // start_date NE diramo iz pending
    // pms_id / pms_account_id NE diramo iz pending
  }

  // Sada ponovno izračunamo display stringove striktno iz mergedFromApi + mapa,
  // BEZ oslanjanja na tempChanges nazive:
  if (Object.prototype.hasOwnProperty.call(mergedFromApi, "pms_id")) {
    const nameForPmsId =
      mergedFromApi.pms_id_name ??
      (mergedFromApi.pms_id != null
        ? houseNameById.get(String(mergedFromApi.pms_id))
        : "") ??
      "";

    mergedFromApi.pms_id_display =
      mergedFromApi.pms_id != null
        ? `${mergedFromApi.pms_id}${nameForPmsId ? ` - ${nameForPmsId}` : ""}`
        : "";
  }

  if (Object.prototype.hasOwnProperty.call(mergedFromApi, "pms_account_id")) {
    const nameForPmsAcc =
      mergedFromApi.pms_account_name ??
      (mergedFromApi.pms_account_id != null
        ? pmsNameById.get(String(mergedFromApi.pms_account_id))
        : "") ??
      "";

    mergedFromApi.pms_account_id_display =
      formatPmsAccount(mergedFromApi.pms_account_id, nameForPmsAcc);
  }

  dispatch(updatePaymentState(mergedFromApi));
  return updatePayment({
    ...mergedFromApi,
    pms_id: getPmsIdApiValue(
      mergedFromApi.pms_id,
      mergedFromApi.pms_id_name
    ),
    pms_account_id: mergedFromApi.pms_account_id,
  });
};


  if (
    tableLoading ||
    cardTypesLoading ||
    houseAccountsLoading ||
    cardTypes2Loading ||
    cardTypes3Loading ||
    (isMews && outletsLoading)
  ) {
    return <Loading />;
  }
  if (dataWithArrows.length === 0 && searchQuery) return <NoSearch />;
  if (dataWithArrows.length === 0) return <NoItems />;

  return (
    <Table
      sortByData={sortByData}
      headerData={headerData}
      rowKeys={rowKeys}
      data={dataWithArrows}
      editable={editable}
      row_key="payment_id"
      updateAction={updateAction}
    />
  );
};

export default PaymentsSNT;
